import Link from 'next/link'
import React, { Dispatch, SetStateAction } from 'react'
import Fuse from 'fuse.js'
import { cn } from '~shared/utils/cn'
import { getCategoriesData } from '~shared/api/categories/getCategories'
import { SearchIcon } from '~shared/icons/search'
import { Program } from '~shared/api/categories/types'
import { SearchLargeIcon } from '~shared/icons/search-large'
import { useClickOutside } from '~shared/utils/useClickOutSide'
import { programLink } from '~shared/utils/program-link'
import { calculateRuCase } from '~shared/utils/calculateRuCase'

type Props = {
  categories: Awaited<ReturnType<typeof getCategoriesData>>
  className?: string
  setIsShort?: Dispatch<SetStateAction<boolean>>
  isDrawer?: boolean
  isShort?: boolean
  isMobile?: boolean
}

const getCheapestCost = (program: Program) => {
  const formats = [
    {
      price: program?.costOnline?.priceOnline,
      discount: program?.costOnline?.discountOnline,
      studyDurationYears: program?.costOnline?.studyDurationYearsOnline,
      studyDurationMonths: program?.costOnline?.studyDurationMonthsOnline,
      studyHours: program?.costOnline?.studyHoursOnline,
      discountMonths: program?.costOnline?.discountMonthsOnline
    },
    {
      price: program?.cost?.price,
      discount: program?.cost?.discount,
      studyDurationYears: program?.cost?.studyDurationYears,
      studyDurationMonths: program?.cost?.studyDurationMonths,
      studyHours: program?.cost?.studyHours,
      discountMonths: program?.cost?.discountMonths
    },
    {
      price: program?.costOffline?.priceOffline,
      discount: program?.costOffline?.discountOffline,
      studyDurationYears: program?.costOffline?.studyDurationYearsOffline,
      studyDurationMonths: program?.costOffline?.studyDurationMonthsOffline,
      studyHours: program?.costOffline?.studyHoursOffline,
      discountMonths: program?.costOffline?.discountMonthsOffline
    }
  ].filter(f => typeof f.price === 'number')

  if (!formats.length) return null

  return formats.reduce((min, current) =>
    Number(current?.price) < Number(min.price) ? current : min
  )
}

export const SearchInput: React.FC<Props> = ({
  categories,
  className,
  setIsShort,
  isDrawer,
  isMobile,
  isShort
}) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [searchedPrograms, setSearchedPrograms] = React.useState<
    Program[] | null
  >(null)
  const [searcheParams, setSearcheParams] = React.useState<string>('')
  const [suggestions, setSuggestions] = React.useState<Program[] | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [searchSlugs, setSearchSlugs] = React.useState('')

  const searchPrograms = (searchParams: string) => {
    const query = searchParams.trim()

    if (!query || !categories?.categories || query.length < 3) {
      setSearchedPrograms([])
      setSuggestions(null)
      return
    }

    const programs = categories.categories.reduce(
      (acc: Program[], category) => {
        const filtered =
          category.programs
            ?.filter(program =>
              program.name?.toLowerCase().includes(query.toLowerCase())
            )
            .map(item => ({
              ...item,
              category: {
                slug: category.slugSingular ?? category.slug,
                name: category.name
              }
            })) || []
        return [...acc, ...filtered]
      },
      []
    )

    setSearchedPrograms(programs)
    setSearchSlugs(
      programs
        .map(el => {
          return el.slug
        })
        .join('--')
    )

    if (programs.length === 0) {
      const allPrograms: Program[] = categories.categories.flatMap(category =>
        (category.programs || []).map(program => ({
          ...program,
          category: {
            slug: category.slugSingular ?? category.slug,
            name: category.name
          }
        }))
      )

      const fuse = new Fuse(allPrograms, {
        keys: ['name'],
        threshold: 0.1,
        minMatchCharLength: 3
      })
      const result = fuse.search(query)
      const matches = result.map(r => r.item)

      if (matches.length === 0) {
        const fallbackFuse = new Fuse(allPrograms, {
          keys: ['name'],
          threshold: 0.5,
          minMatchCharLength: 2
        })
        const fallbackResult = fallbackFuse.search(query)
        setSuggestions(fallbackResult.map(r => r.item).slice(0, 5))
      } else {
        setSuggestions(null)
      }
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
    if (setIsShort) setIsShort(true)
    inputRef.current?.focus()
  }
  useClickOutside(containerRef, () => {
    setIsFocused(false)
    if (setIsShort) setIsShort(false)
  })

  return (
    <div
      className={cn(
        'relative z-20 ',
        className,
        isFocused
          ? 'w-full duration-100 ease-in-out transition-all'
          : 'sm:min-w-[65px] min-w-[50px]'
      )}
      role='search'
      aria-expanded={isFocused}
      tabIndex={0}
      onTouchStart={handleFocus}
      onClick={handleFocus}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleFocus()
        }
      }}
      ref={containerRef}>
      <div
        className={cn(
          'z-30 relative border flex px-[8px] sm:px-[16px] md:px-[24px] rounded-[24px] gap-[10px]  items-center h-full',
          isFocused
            ? 'border-black text-black gap-[10px]'
            : 'border-[#8B8B8B] text-[#8B8B8B] ',
          isMobile && 'bg-[#F0F0F0] border-[#F0F0F0] gap-[10px]',
          !isShort && isMobile && 'justify-center'
        )}>
        <span className='min-w-[16px] min-h-[16px]'>
          <SearchIcon className='hidden md:inline-block' />
          <SearchLargeIcon className='md:hidden' />
        </span>
        {isMobile ? (
          isShort && (
            <input
              className={cn(
                'w-full max-w-[300px] cursor-pointer',
                isMobile && 'bg-[#F0F0F0]'
              )}
              type='search'
              id='header-search-input-mobile'
              name='search'
              value={searcheParams}
              placeholder='поиск'
              ref={inputRef}
              onChange={e => {
                searchPrograms(e.target.value)
                setSearcheParams(e.target.value)
              }}
            />
          )
        ) : (
          <input
            className={cn(
              'w-full max-w-[300px] cursor-pointer',
              isMobile && 'bg-[#F0F0F0]'
            )}
            type='search'
            id='header-search-input-desktop'
            name='search'
            placeholder='поиск'
            value={searcheParams}
            ref={inputRef}
            onChange={e => {
              searchPrograms(e.target.value)
              setSearcheParams(e.target.value)
            }}
          />
        )}
      </div>

      {isFocused && searcheParams.length > 2 && (
        <div
          className='absolute rounded-[24px] p-[16px]  pt-[80px]  shadow-xl top-0 left-0 w-full bg-white z-20'
          onTouchStart={e => e.stopPropagation()}>
          <div className='overflow-y-auto scrollbar-always max-h-[190px] flex flex-col gap-[16px]'>
            {searchedPrograms &&
              searchedPrograms.length > 0 &&
              searchedPrograms.slice(0, 10).map(item => {
                const cheapestCost = getCheapestCost(item)

                return (
                  <Link
                    key={item.slug}
                    className='block mr-[13px]'
                    prefetch={false}
                    href={programLink(item)}>
                    <div
                      className={cn(
                        isDrawer &&
                          'p-[5px] pl-[12px] hover:bg-[#0000000D] rounded-[8px]'
                      )}>
                      <p className='text-[14px] md:text-[16px] font-medium mb-[8px]'>
                        {item.name}
                      </p>
                      <p className='text-[12px]  text-[#6A6A6A]'>
                        {Number(cheapestCost?.studyDurationYears) > 0 ? (
                          <span>
                            {cheapestCost?.studyDurationYears}{' '}
                            {calculateRuCase(
                              'год',
                              cheapestCost?.studyDurationYears ?? 0,
                              'ru'
                            )}{' '}
                          </span>
                        ) : null}
                        {Number(cheapestCost?.studyDurationYears) > 0 &&
                        Number(cheapestCost?.studyDurationMonths) > 0 ? (
                          <span>/ </span>
                        ) : null}
                        {Number(cheapestCost?.studyDurationMonths) > 0 ? (
                          <span>{cheapestCost?.studyDurationMonths} мес </span>
                        ) : null}
                        {Number(cheapestCost?.studyHours) > 0 ? (
                          <span>/ {cheapestCost?.studyHours} часов </span>
                        ) : null}
                      </p>
                    </div>
                  </Link>
                )
              })}

            {searchedPrograms &&
              searchedPrograms.length === 0 &&
              suggestions &&
              suggestions.length > 0 && (
                <div>
                  <div className='mb-[20px] font-medium'>
                    <p>К сожалению по вашему запросу ничего не найдено.</p>
                    <p className='text-[12px] text-[#6A6A6A] mt-[8px]'>
                      Может быть вы искали:
                    </p>
                  </div>
                  <ul className='flex flex-wrap'>
                    {suggestions.map(item => (
                      <li key={item.slug}>
                        <Link
                          prefetch={false}
                          className='bg-[#F2F2F2] flex items-center rounded-[24px] text-[14px] mr-[8px] mb-[8px] px-[16px] py-[12px] hover:bg-[#3A52EE] hover:text-white'
                          href={programLink(item)}>
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {searchedPrograms &&
              searchedPrograms.length === 0 &&
              (!suggestions || suggestions.length === 0) && (
                <p className='font-medium leading-[25px]'>
                  К сожалению по вашему запросу не найдено совпадений.
                  Попробуйте изменить запрос
                </p>
              )}
          </div>

          {searchedPrograms && searchedPrograms.length > 3 && (
            <Link
              prefetch={false}
              href={`/programmy?query=${searchSlugs}`}
              className={cn(
                'bg-[#3A52EE] text-white rounded-[51px] px-[16px] mt-[20px] inline-block  mb-[10px] py-[12px] hover:bg-black',
                isDrawer && !isMobile && 'ml-[10px]'
              )}>
              смотреть все
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
